import React, { useEffect, useMemo, useState } from "react";
import InteractiveMap from "./map/InteractiveMap";
import {
    MapPin,
    Copy,
    Check,
    X,
    ExternalLink,
    Navigation,
    Home,
    User,
    Phone,
    Building2,
    CalendarClock,
} from "lucide-react";

const DEFAULT_ZOOM = 17;

/**
 * تشخیص اینکه یک جفت مختصات واقعاً ثبت شده است یا نه.
 * طبق مستندات، null بودن هر دو مقدار یعنی موقعیت ثبت نشده است.
 * مقدار 0,0 هم به‌عنوان «ثبت نشده» در نظر گرفته می‌شود چون در داده‌های قدیمی
 * به‌جای null ذخیره شده است. از این تابع در همه جا استفاده شود تا شرط
 * نمایش دکمه نقشه با محتوای مودال یکسان بماند.
 */
export const hasValidLocation = (
    lat?: number | null,
    lng?: number | null
): boolean => {
    if (lat == null || lng == null) return false;
    if (Number.isNaN(Number(lat)) || Number.isNaN(Number(lng))) return false;
    if (Number(lat) === 0 && Number(lng) === 0) return false;
    return true;
};

export interface LocationModalInfo {
    label: string;
    value?: string | number | null;
    icon?: "user" | "phone" | "home" | "city" | "date";
}

interface LocationModalProps {
    show: boolean;
    onHide: () => void;
    lat?: number | null;
    lng?: number | null;
    title?: string;
    /** اطلاعات تکمیلی که زیر نقشه نمایش داده می‌شود */
    info?: LocationModalInfo[];
    /** آدرس ثبت‌شده در سامانه (در صورت وجود) */
    registeredAddress?: string | null;
}

const iconMap = {
    user: User,
    phone: Phone,
    home: Home,
    city: Building2,
    date: CalendarClock,
};

const LocationModal: React.FC<LocationModalProps> = ({
    show,
    onHide,
    lat,
    lng,
    title = "موقعیت مکانی",
    info = [],
    registeredAddress,
}) => {
    const [copied, setCopied] = useState<"coords" | null>(null);
    const [zoom, setZoom] = useState(DEFAULT_ZOOM);

    const hasLocation = hasValidLocation(lat, lng);

    // نکته: آدرس نمایشی از پروفایل خبازی (registeredAddress) می‌آید و
    // Frontend نباید خودش با reverse-geocoding آدرس بسازد.

    useEffect(() => {
        if (show && hasLocation) {
            setZoom(DEFAULT_ZOOM);
        }
    }, [show, hasLocation]);

    // ===== بستن با کلید Esc =====
    useEffect(() => {
        if (!show) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onHide();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [show, onHide]);

    const coordsText = useMemo(
        () => (hasLocation ? `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}` : ""),
        [lat, lng, hasLocation]
    );

    const handleCopy = (text: string, kind: "coords") => {
        if (!text) return;
        const done = () => {
            setCopied(kind);
            setTimeout(() => setCopied(null), 1800);
        };
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(text).then(done).catch(() => { });
        } else {
            const el = document.createElement("textarea");
            el.value = text;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
            done();
        }
    };

    if (!show) return null;

    return (
        <React.Fragment>
            <style>{`
                .havir-pin { position: relative; width: 28px; height: 28px; }
                .havir-pin__dot {
                    position: absolute; inset: 8px; border-radius: 9999px;
                    background: #ef4444; border: 3px solid #fff;
                    box-shadow: 0 2px 8px rgba(0,0,0,.35);
                }
                .havir-pin__pulse {
                    position: absolute; inset: 0; border-radius: 9999px;
                    background: rgba(239,68,68,.35);
                    animation: havirPulse 1.8s ease-out infinite;
                }
                @keyframes havirPulse {
                    0%   { transform: scale(.5); opacity: .9; }
                    70%  { transform: scale(1.6); opacity: 0; }
                    100% { transform: scale(1.6); opacity: 0; }
                }
                .havir-loc-modal { animation: havirZoomIn .22s ease-out; }
                @keyframes havirZoomIn {
                    from { opacity: 0; transform: translateY(12px) scale(.97); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                .leaflet-container { font-family: inherit; }
            `}</style>

            {/* z-[10050] بالاتر از همه مودال‌های پروژه است (z-drawer=1050 و مودال‌های z-[9999])
                تا وقتی از داخل یک مودال دیگر باز می‌شود، روی آن بنشیند نه زیرش */}
            <div
                dir="rtl"
                className="fixed inset-0 z-[10050] flex items-center justify-center p-3 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
                onClick={onHide}
            >
                <div
                    className="havir-loc-modal w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col bg-white dark:bg-zink-700 rounded-2xl shadow-2xl ring-1 ring-black/5"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* ===== هدر ===== */}
                    <div className="relative px-5 py-4 bg-gradient-to-l from-emerald-600 via-teal-600 to-cyan-600 text-white">
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <span className="flex items-center justify-center rounded-xl size-10 bg-white/20 backdrop-blur">
                                    <MapPin size={20} />
                                </span>
                                <div>
                                    <h5 className="text-base font-bold leading-6">{title}</h5>
                                    <p className="text-xs text-white/80">
                                        {hasLocation ? "موقعیت ثبت‌شده روی نقشه" : "موقعیتی ثبت نشده است"}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onHide}
                                className="flex items-center justify-center transition rounded-lg size-9 bg-white/15 hover:bg-white/30"
                                title="بستن"
                            >
                                <X size={18} />
                            </button>
                        </div>
                    </div>

                    {/* ===== بدنه ===== */}
                    <div className="p-4 space-y-4 overflow-y-auto sm:p-5">
                        {!hasLocation ? (
                            <div className="flex flex-col items-center justify-center gap-3 py-14 text-slate-400">
                                <MapPin size={44} className="opacity-40" />
                                <p className="text-sm">برای این رکورد موقعیت مکانی ثبت نشده است.</p>
                            </div>
                        ) : (
                            <React.Fragment>
                                {/* نقشه — Leaflet با کاشی‌های رایگان و بی‌کلید */}
                                <div className="overflow-hidden border rounded-xl border-slate-200 dark:border-zink-500">
                                    <InteractiveMap
                                        lat={Number(lat)}
                                        lng={Number(lng)}
                                        height={330}
                                        initialZoom={DEFAULT_ZOOM}
                                        onZoomChange={setZoom}
                                    />
                                </div>

                                {/* مختصات */}
                                <div className="flex flex-wrap items-center gap-2 p-4 border rounded-xl bg-slate-50 dark:bg-zink-600 border-slate-200 dark:border-zink-500">
                                    <span className="px-2.5 py-1 font-mono text-xs rounded-lg bg-white dark:bg-zink-700 text-slate-600 dark:text-zink-200 border border-slate-200 dark:border-zink-500" dir="ltr">
                                        {coordsText}
                                    </span>
                                    <button
                                        onClick={() => handleCopy(coordsText, "coords")}
                                        className="flex items-center gap-1 px-2.5 py-1 text-xs transition rounded-lg text-slate-600 dark:text-zink-200 hover:bg-slate-200 dark:hover:bg-zink-500"
                                    >
                                        {copied === "coords" ? (
                                            <React.Fragment>
                                                <Check size={13} className="text-emerald-600" /> کپی شد
                                            </React.Fragment>
                                        ) : (
                                            <React.Fragment>
                                                <Copy size={13} /> کپی مختصات
                                            </React.Fragment>
                                        )}
                                    </button>
                                </div>
                            </React.Fragment>
                        )}

                        {/* آدرس ثبت‌شده در سامانه */}
                        {registeredAddress && (
                            <div className="flex items-start gap-2 p-4 text-sm border rounded-xl border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800/50">
                                <Home size={15} className="mt-0.5 text-amber-600 shrink-0" />
                                <div>
                                    <span className="block mb-1 font-semibold text-amber-800 dark:text-amber-300">
                                        آدرس ثبت‌شده نانوایی
                                    </span>
                                    <span className="leading-6 text-amber-900 dark:text-amber-200">{registeredAddress}</span>
                                </div>
                            </div>
                        )}

                        {/* اطلاعات تکمیلی */}
                        {info.filter((i) => i.value != null && i.value !== "").length > 0 && (
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                {info
                                    .filter((i) => i.value != null && i.value !== "")
                                    .map((item, idx) => {
                                        const Icon = item.icon ? iconMap[item.icon] : MapPin;
                                        return (
                                            <div
                                                key={idx}
                                                className="flex items-center gap-2 px-3 py-2.5 text-sm border rounded-xl border-slate-200 dark:border-zink-500 bg-white dark:bg-zink-600"
                                            >
                                                <Icon size={15} className="text-slate-400 shrink-0" />
                                                <span className="text-slate-500 dark:text-zink-200">{item.label}:</span>
                                                <span className="font-medium truncate text-slate-800 dark:text-zink-50">
                                                    {item.value}
                                                </span>
                                            </div>
                                        );
                                    })}
                            </div>
                        )}
                    </div>

                    {/* ===== فوتر ===== */}
                    <div className="flex flex-wrap items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-zink-500 bg-slate-50 dark:bg-zink-600">
                        {hasLocation && (
                            <React.Fragment>
                                {/* مسیریابی تا همان مختصاتی که روی نقشه نمایش داده شده است */}
                                <a
                                    href={`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white transition rounded-lg bg-emerald-600 hover:bg-emerald-700"
                                >
                                    <Navigation size={15} /> مسیریابی
                                </a>
                                <a
                                    href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=${zoom}/${lat}/${lng}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition border rounded-lg text-emerald-700 border-emerald-300 bg-emerald-50 hover:bg-emerald-100"
                                >
                                    <ExternalLink size={15} /> OpenStreetMap
                                </a>
                                <a
                                    href={`https://www.google.com/maps?q=${lat},${lng}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-blue-700 transition border border-blue-300 rounded-lg bg-blue-50 hover:bg-blue-100"
                                >
                                    <ExternalLink size={15} /> گوگل‌مپ
                                </a>
                            </React.Fragment>
                        )}
                        <button
                            onClick={onHide}
                            className="px-4 py-2 text-sm font-medium transition rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-zink-500 dark:text-zink-100"
                        >
                            بستن
                        </button>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default LocationModal;
